import { useSupportStore } from '@/stores/supports/useSupportStore';
import { onUnmounted, ref } from '@vue/runtime-core';
import { useToast } from 'vue-toastification';

export interface PdfJob {
  jobId: string;
  supportId: number;
  supportName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  downloadUrl?: string;
  error?: string;
  createdAt: Date;
}

export function usePdfJobManager() {
  const toast = useToast();
  const supportStore = useSupportStore();
  
  // État local
  const activeJobs = ref<Map<string, PdfJob>>(new Map());
  const isPolling = ref(false);
  const pollingInterval = ref<NodeJS.Timeout | null>(null);

  // Lancer la génération PDF haute qualité
  const requestHighQualityPdf = async (supportId: number, supportName: string) => {
    try {
      const response = await supportStore.requestHighQualityPdfGeneration(supportId);
      
      // Vérifier que la réponse contient un job_id valide
      // Le backend renvoie job_id dans response.data.job_id
      const jobId = response?.data?.job_id || response?.job_id;
      if (!jobId) {
        throw new Error('La réponse du serveur ne contient pas d\'ID de job valide');
      }
      
      const job: PdfJob = {
        jobId: jobId,
        supportId,
        supportName,
        status: 'pending',
        progress: 0,
        createdAt: new Date(),
      };

      activeJobs.value.set(jobId, job);
      
      // Démarrer le polling si pas déjà actif
      if (!isPolling.value) {
        startPolling();
      }

      toast.success(`Génération PDF haute qualité lancée pour "${supportName}"`);
      
      return jobId;
    } catch (error) {
      toast.error(`Erreur lors du lancement de la génération PDF : ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      throw error;
    }
  };

  // Nettoyer les jobs avec des IDs invalides
  const cleanupInvalidJobs = () => {
    const invalidJobs: string[] = [];
    
    activeJobs.value.forEach((job, jobId) => {
      if (!jobId || jobId === 'undefined' || jobId.trim() === '' || !job.jobId || job.jobId === 'undefined') {
        invalidJobs.push(jobId);
      }
    });
    
    invalidJobs.forEach(jobId => {
      console.warn(`Suppression d'un job avec un ID invalide: ${jobId}`);
      activeJobs.value.delete(jobId);
    });
    
    if (invalidJobs.length > 0) {
      console.log(`${invalidJobs.length} job(s) invalide(s) supprimé(s)`);
    }
  };

  // Démarrer le polling des jobs actifs
  const startPolling = () => {
    if (isPolling.value) return;
    
    // Nettoyer les jobs invalides avant de commencer le polling
    cleanupInvalidJobs();
    
    isPolling.value = true;
    pollingInterval.value = setInterval(async () => {
      await checkAllActiveJobs();
    }, 3000); // Vérifier toutes les 3 secondes
  };

  // Arrêter le polling
  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }
    isPolling.value = false;
  };

  // Vérifier tous les jobs actifs
  const checkAllActiveJobs = async () => {
    const jobsToCheck = Array.from(activeJobs.value.values())
      .filter(job => job.status === 'pending' || job.status === 'processing');

    if (jobsToCheck.length === 0) {
      stopPolling();
      return;
    }

    for (const job of jobsToCheck) {
      // Vérifier que le jobId existe avant de faire l'appel API
      if (!job.jobId) {
        console.error(`Job sans ID trouvé, suppression du job:`, job);
        // Supprimer tous les jobs avec des clés undefined ou vides
        for (const [key, value] of activeJobs.value.entries()) {
          if (!key || !value.jobId) {
            activeJobs.value.delete(key);
          }
        }
        continue;
      }

      try {
        const status = await supportStore.checkPdfJobStatus(job.jobId);
        updateJobStatus(job.jobId, status);
      } catch (error) {
        console.error(`Erreur lors de la vérification du job ${job.jobId}:`, error);
        // En cas d'erreur, marquer le job comme échoué après 5 minutes
        const now = new Date();
        const jobAge = now.getTime() - job.createdAt.getTime();
        if (jobAge > 5 * 60 * 1000) { // 5 minutes
          updateJobStatus(job.jobId, {
            status: 'failed',
            progress: 0,
            error: 'Timeout - Le job a pris trop de temps'
          });
        }
      }
    }
  };

  // Mettre à jour le statut d'un job
  const updateJobStatus = (jobId: string, statusData: any) => {
    const job = activeJobs.value.get(jobId);
    if (!job) return;

    // Sauvegarder l'ancien statut pour détecter les changements
    const previousStatus = job.status;

    const updatedJob = {
      ...job,
      status: statusData.status,
      progress: statusData.progress || job.progress,
      downloadUrl: statusData.download_url,
      error: statusData.error,
    };

    activeJobs.value.set(jobId, updatedJob);

    // Notifications selon le statut
    switch (statusData.status) {
      case 'processing':
        // Notification uniquement si c'est un changement de statut (de pending à processing)
        if (previousStatus === 'pending') {
          toast.info(
            `Génération PDF en cours pour "${job.supportName}" (${statusData.progress || 0}%)`,
            { timeout: 5000 }
          );
        }
        break;
      case 'completed':
        toast.success(
          `PDF haute qualité prêt pour "${job.supportName}" !`,
          {
            timeout: 10000,
            closeButton: true,
            onClick: () => {
              if (updatedJob.downloadUrl) {
                window.open(updatedJob.downloadUrl, '_blank');
              }
            }
          }
        );
        break;
      case 'failed':
        toast.error(
          `Échec de la génération PDF pour "${job.supportName}" : ${statusData.error || 'Erreur inconnue'}`,
          { timeout: 8000 }
        );
        break;
    }
  };

  // Télécharger un PDF terminé
  const downloadPdf = (jobId: string) => {
    const job = activeJobs.value.get(jobId);
    if (job && job.status === 'completed' && job.downloadUrl) {
      window.open(job.downloadUrl, '_blank');
    }
  };

  // Supprimer un job de la liste
  const removeJob = (jobId: string) => {
    activeJobs.value.delete(jobId);
    
    // Arrêter le polling si plus de jobs actifs
    const hasActiveJobs = Array.from(activeJobs.value.values())
      .some(job => job.status === 'pending' || job.status === 'processing');
    
    if (!hasActiveJobs) {
      stopPolling();
    }
  };

  // Nettoyer les jobs terminés de plus de 30 minutes
  const cleanupOldJobs = () => {
    // D'abord nettoyer les jobs invalides
    cleanupInvalidJobs();
    
    const now = new Date();
    const jobsToRemove: string[] = [];

    activeJobs.value.forEach((job, jobId) => {
      const jobAge = now.getTime() - job.createdAt.getTime();
      const isOld = jobAge > 30 * 60 * 1000; // 30 minutes
      const isCompleted = job.status === 'completed' || job.status === 'failed';
      
      if (isOld && isCompleted) {
        jobsToRemove.push(jobId);
      }
    });

    jobsToRemove.forEach(jobId => removeJob(jobId));
  };

  // Nettoyage automatique toutes les 5 minutes
  const cleanupInterval = setInterval(cleanupOldJobs, 5 * 60 * 1000);

  // Nettoyage à la destruction du composable
  onUnmounted(() => {
    stopPolling();
    if (cleanupInterval) {
      clearInterval(cleanupInterval);
    }
  });

  return {
    // État
    activeJobs,
    isPolling,
    
    // Actions
    requestHighQualityPdf,
    downloadPdf,
    removeJob,
    cleanupOldJobs,
    cleanupInvalidJobs,
  };
}